import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";

export default function CountrySelect({ open, onClose, forceOpen }) {
  const { setCountry, setCurrency, country } = useApp();
  const [countries, setCountries] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => { api.get("/countries").then((r) => setCountries(r.data)).catch(() => {}); }, []);

  const choose = (c) => {
    setCountry({ code: c.code, name: c.name });
    setCurrency(c.currency);
    onClose && onClose();
    setShowAll(false);
  };

  const primary = countries.filter((c) => ["CA", "US", "GB"].includes(c.code));
  const rest = countries.filter((c) => !["CA", "US", "GB"].includes(c.code));
  const filtered = rest.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()));
  const visible = open || forceOpen;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="country-select-overlay"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="fixed inset-0 z-[100] flex items-center justify-center px-6"
          style={{ background: "#0F2F24", color: "#F3EBDD" }}
        >
          {!forceOpen && country && (
            <button data-testid="country-close" onClick={onClose}
              className="absolute top-7 right-7 text-cream/70 hover:text-cream transition-colors">
              <X size={26} strokeWidth={1} />
            </button>
          )}
          <motion.div
            initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-xl text-center"
          >
            <div className="eyebrow mb-8" style={{ color: "rgba(243,235,221,0.55)" }}>EST. 1997 · ONLINE MAISON</div>
            <h1 className="font-serif italic leading-none mb-14"
              style={{ fontSize: "clamp(2.8rem, 8vw, 5rem)", letterSpacing: "0.01em" }}>
              Maison Évaren
            </h1>

            {!showAll ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
                <div className="eyebrow mb-3" style={{ color: "rgba(243,235,221,0.9)" }}>Select Your Country</div>
                <p className="font-serif italic text-xl mb-12" style={{ color: "rgba(243,235,221,0.65)" }}>
                  Choose your shopping destination
                </p>
                <div className="space-y-px">
                  {primary.map((c) => (
                    <button key={c.code} data-testid={`country-${c.code}`} onClick={() => choose(c)}
                      className="group w-full py-5 border-t transition-colors"
                      style={{ borderColor: "#2A4E3F" }}>
                      <span className="font-serif text-2xl md:text-3xl transition-all duration-500 group-hover:tracking-widest"
                        style={{ color: "#F3EBDD" }}>{c.name}</span>
                    </button>
                  ))}
                  <button data-testid="country-other" onClick={() => setShowAll(true)}
                    className="group w-full py-5 border-t border-b transition-colors" style={{ borderColor: "#2A4E3F" }}>
                    <span className="font-serif text-2xl md:text-3xl transition-all duration-500 group-hover:tracking-widest"
                      style={{ color: "rgba(243,235,221,0.8)" }}>Other</span>
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="flex items-center gap-3 border-b pb-3 mb-6 max-w-md mx-auto"
                  style={{ borderColor: "#2A4E3F" }}>
                  <Search size={18} strokeWidth={1} style={{ color: "rgba(243,235,221,0.6)" }} />
                  <input data-testid="country-search" autoFocus value={query}
                    onChange={(e) => setQuery(e.target.value)} placeholder="Search countries"
                    className="bg-transparent outline-none w-full font-sans text-sm tracking-wider placeholder:text-[rgba(243,235,221,0.4)]"
                    style={{ color: "#F3EBDD" }} />
                </div>
                <div className="max-h-[46vh] overflow-y-auto no-scrollbar text-left max-w-md mx-auto">
                  {filtered.map((c) => (
                    <button key={c.code} data-testid={`country-${c.code}`} onClick={() => choose(c)}
                      className="w-full flex items-center justify-between py-3.5 border-t transition-colors hover:px-2"
                      style={{ borderColor: "rgba(42,78,63,0.6)" }}>
                      <span className="font-serif text-lg" style={{ color: "#F3EBDD" }}>{c.name}</span>
                      <span className="text-xs tracking-widest" style={{ color: "rgba(243,235,221,0.45)" }}>{c.currency}</span>
                    </button>
                  ))}
                </div>
                <button onClick={() => setShowAll(false)}
                  className="mt-8 eyebrow" style={{ color: "rgba(243,235,221,0.6)" }}>← Back</button>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
